import React, { FC, useCallback, useState, useMemo } from 'react';
import styles from './index.module.scss';
import { useTranslation } from 'react-i18next';
import { Form, notification, Input, Button } from 'src/common';
import { usePasswordValidator } from 'src/helpers/passwords';
import { Auth } from 'aws-amplify';
import { InfoLabel } from 'src/shared';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';

interface IFormState {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const PasswordSettings: FC = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const initialValues = useMemo((): IFormState => {
    return {
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
  }, []);

  const onFinish = useCallback(
    (values: unknown) => {
      setIsLoading(true);
      const { oldPassword, newPassword } = values as IFormState;
      Auth.currentAuthenticatedUser()
        .then(user => {
          Auth.changePassword(user, oldPassword, newPassword)
            .catch(() => {
              notification.error({
                message: t('Common.unknownError'),
                description: t('Common.unknownErrorDesc')
              });
            })
            .finally(() => {
              form.resetFields();
              setIsLoading(false);
            });
        })
        .catch(() => {
          notification.error({
            message: t('Common.unknownError'),
            description: t('Common.unknownErrorDesc')
          });
        });
    },
    [form, t]
  );

  const passwordRule = usePasswordValidator();

  return (
    <div className={styles.contentInner}>
      <h3 className={styles.contentTitle}>
        <span>{t('Common.password')}</span>
      </h3>
      <p className={styles.contentDesc}>{t('Account.passwordFormDesc')}</p>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={onFinish}
        className={styles.formContent}
        layout={'vertical'}
      >
        <div className={styles.grid}>
          <Form.Item
            name="oldPassword"
            label={t('Account.oldPassword')}
            rules={[{ required: true, message: t('Account.enterOldPassword') }]}
          >
            <Input.Password
              autoComplete={'current-password'}
              type={'password'}
              placeholder={t('Account.oldPassword')}
              iconRender={visible => <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />}
            />
          </Form.Item>
        </div>
        <div className={styles.grid}>
          <Form.Item
            name="newPassword"
            label={<InfoLabel title={t('Auth.newPassword')} desc={t('Auth.passwordDescription')} />}
            rules={[
              { required: true, message: t('Auth.enterNewPassword') },
              { validator: passwordRule, message: t('Auth.passwordPolicy') }
            ]}
          >
            <Input.Password
              placeholder={t('Auth.newPassword')}
              autoComplete={'new-password'}
              type={'password'}
              iconRender={visible => <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />}
            />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t('Account.confirmNewPassword')}
            dependencies={['newPassword']}
            rules={[
              { required: true, message: t('Auth.enterNewPassword') },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(t('Auth.passwordMustBeTheSame'));
                }
              })
            ]}
          >
            <Input.Password
              placeholder={t('Account.confirmNewPassword')}
              autoComplete={'new-password'}
              type={'password'}
              iconRender={visible => <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />}
            />
          </Form.Item>
        </div>
        <div className={styles.formSubmit}>
          <Button type={'primary'} htmlType={'submit'} loading={isLoading}>
            {t('Account.changeYourPassword')}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default PasswordSettings;
